/**
 * components/SyncNotice.js
 * What the last merge with someone else's changes did (multi-device sync, step 5a) — see
 * utils/remoteSync.js. Shown at the top of the page on the shared online books only.
 *   - everything merged → a quiet blue note naming who changed the books; it hides itself
 *     after 10 seconds (it's information, not a problem);
 *   - something of ours could NOT be re-applied → a red list naming each change and why, which
 *     stays until closed. Those changes are not saved — the list is the only place that says so,
 *     so it must never disappear on its own.
 */
import React, { useEffect, useRef } from "react";

const AUTO_HIDE_MS = 10000;

export function failureText(f, by) {
  switch (f.reason) {
    case "changed":
      return `${f.label}: ${f.detail || `${f.what || "data ini"} diubah oleh ${by} saat Anda sedang mengubahnya`}. `
        + "Perubahan ini belum tersimpan — buka lagi untuk melihat versi terbaru, lalu ulangi bila masih perlu.";
    case "cannot_merge":
      return `${f.label}: tidak bisa digabung otomatis karena data bersama baru saja berubah. Perubahan ini belum tersimpan — ulangi langkah ini.`;
    case "code_taken":
      return `${f.label}: kode barang ${(f.codes || []).join(", ")} sudah dipakai di perangkat lain. Perubahan ini belum tersimpan — ulangi langkah ini.`;
    default:
      return `${f.label}: tidak bisa diterapkan lagi pada data terbaru (datanya sudah diubah atau dihapus di perangkat lain). Perubahan ini belum tersimpan.`;
  }
}

export default function SyncNotice({ notice, onClose }) {
  const failures = notice.failures || [];
  const codeChanges = notice.codeChanges || [];
  const quiet = failures.length === 0 && codeChanges.length === 0;
  const by = notice.updatedBy ? `"${notice.updatedBy}"` : "perangkat lain";

  // Through a ref: the parent re-renders often, and a new onClose each time must not keep
  // restarting the 10-second timer.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!quiet) return undefined;
    const t = setTimeout(() => onCloseRef.current(), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [quiet, notice]);

  return (
    <div className={`alert ${failures.length ? "alert--error" : "alert--info"} sync-banner`} role={failures.length ? "alert" : "status"}>
      <div>
        <div>
          Data diperbarui dengan perubahan dari {by}.
          {notice.keptCount > 0 && " Perubahan Anda sudah digabungkan dan disimpan."}
        </div>
        {codeChanges.map((c, i) => (
          <div key={`c${i}`} style={{ marginTop: "var(--gap-xs)" }}>
            {c.label}: kode barang baru berubah dari {c.from} menjadi {c.to}, karena {c.from} sudah dipakai di perangkat lain.
          </div>
        ))}
        {failures.length > 0 && (
          <ul style={{ margin: "var(--gap-sm) 0 0", paddingLeft: 18 }}>
            {failures.map((f, i) => <li key={i}>{failureText(f, by)}</li>)}
          </ul>
        )}
      </div>
      <button type="button" className="btn" onClick={onClose}>Tutup</button>
    </div>
  );
}
